import moment from "moment"
import React from "react"
import { Button, Col, Form, Row, Toast, ToastContainer } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "../../app/hooks"
import CheckCircleIcon from "../../assets/checkCircleIcon.svg"
import DownloadIcon from "../../assets/downloadIcon.svg"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import RemotePagination from "../../components/Tables/RemotePagination"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { downloadFiles } from "../../utils/commonFunctions"

const IncomingOrdersTable = ({
    orders,
    parcels = false,
    setOptionsForListing,
    onSearch,
    setKeyword,
    total,
    page,
    size,
    keyword,
    selectedParcels,
    setSelectedParcels,
    regenerateLabels,
    toast,
    setToast,
    reqLoading
}: {
    orders: Record<string, any>[]
    setOptionsForListing: Function
    onSearch: Function
    setKeyword: Function
    total: number
    selectedParcels: number[]
    setSelectedParcels: Function
    regenerateLabels: Function
    toast: Record<string, any>
    setToast: Function
    parcels?: boolean
    page?: number
    size?: number
    keyword?: string
    reqLoading: boolean
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    function parcelCreatedDate(cell: string) {
        if (cell) return moment(cell).format("DD MMM yyyy")
    }

    const handleTableChange = (page: number, size: number) => {
        setOptionsForListing({
            page,
            size,
            keyword
        })
    }

    const orderColumns = [
        {
            dataField: "created_at",
            text: t("Uploaded on"),
            classes: "date-format",
            formatter: (cell: string, row: Record<string, any>) => {
                return parcelCreatedDate(cell)
            }
        },
        {
            dataField: parcels ? "parcel_id" : "list_id",
            text: parcels ? t("Parcel ID") : t("List ID"),
            classes: "highlighted-text"
        },
        {
            dataField: parcels ? "uuid" : "file_name",
            text: parcels ? t("UUID") : t("Files Name"),
            classes: "highlighted-text"
        },
        {
            dataField: "user_pin",
            text: "Account no."
        },
        {
            dataField: "parcels",
            text: t("Total Parcels"),
            formatter: (cell: any, row: Record<string, any>) => {
                return cell?.length || 0
            }
        },
        {
            dataField: "received_parcels",
            text: t("Parcels Recieved"),
            hidden: true
        },
        {
            dataField: "awb_status",
            text: t("AWB Status"),
            formatter: (cell: number, row: Record<string, any>) => {
                let colorClass = ""
                if (cell === 3) {
                    colorClass = "delayed-payment"
                } else if (cell === 1) {
                    colorClass = "in-process"
                } else if (cell === 2) {
                    colorClass = "active_status"
                } else if (cell === 4) {
                    colorClass = "received"
                } else {
                    colorClass = "received"
                }
                return <span className={colorClass}>{t(`${cell}-awbStatus`)}</span>
            }
        }
    ]

    const onSelect = (row: Record<string, any>, isSelect: any) => {
        if (isSelect) {
            setSelectedParcels([...selectedParcels, row?.parcel_id])
        } else {
            setSelectedParcels(selectedParcels?.filter((sp) => sp !== row?.parcel_id))
        }
    }
    const handleSelectAll = (isSelect: any, rows: Record<string, any>[]) => {
        if (isSelect) {
            setSelectedParcels(rows?.map((sp) => sp?.parcel_id))
        } else {
            setSelectedParcels([])
        }
    }

    const selectRow: any = {
        mode: "checkbox",
        clickToSelect: true,
        onSelect: onSelect,
        onSelectAll: handleSelectAll,
        selectColumnPosition: "left"
    }

    const expandRow = {
        renderer: (row: Record<string, any>) => {
            const columns = [
                {
                    dataField: "purchase_order_number",
                    text: t("Order Number"),
                    classes: "highlighted-text"
                },
                {
                    dataField: "uuid",
                    text: t("Parcel ID"),
                    classes: "highlighted-text"
                },
                {
                    dataField: "recipient_name",
                    text: t("Recipient Name")
                },
                {
                    dataField: "quantity",
                    text: t("Qty")
                },
                {
                    text: t("Status"),
                    classes: "font-12",
                    formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                        let colorClass = row?.reason
                            ?.toLowerCase()
                            ?.replace(/[^a-zA-Z ]/g, " ")
                            ?.split(" ")
                            ?.join("-")

                        return <div className={colorClass}>{t(`${row?.reason}`)}</div>
                    }
                },
                {
                    dataField: "parcel_created_at",
                    text: t("Recieved Date"),
                    classes: "date-format",
                    formatter: (cell: string, row: Record<string, any>) => {
                        return parcelCreatedDate(cell)
                    }
                },
                {
                    dataField: "new_awb_number",
                    text: t("Shopini AWB"),
                    formatter: (cell: string, row: Record<string, any>) => {
                        return cell ? (
                            <div className="d-flex align-items-center">
                                <span>{cell}</span>
                                &nbsp;
                                {row?.awb_link && row?.awb_link !== "" ? (
                                    <img
                                        onClick={() => {
                                            // window.open(`${row?.awb_link}`, "_blank")
                                            downloadFiles(row?.awb_link)
                                        }}
                                        className={`tr-pointer`}
                                        src={DownloadIcon}
                                    />
                                ) : null}
                            </div>
                        ) : (
                            "-"
                        )
                    }
                },
                {
                    dataField: "purchase_tracking_number",
                    text: t("Inbound AWB"),
                    formatter: (cell: string, row: Record<string, any>) => {
                        return <>{cell && cell !== "" ? cell : "-"}</>
                    }
                },
                {
                    dataField: "awb_file_exist",
                    text: t("AWB FILE"),
                    formatter: (cell: number, row: Record<string, any>) => {
                        return (
                            <Form.Check
                                type="checkbox"
                                className="awb-file"
                                checked={cell === 0 ? false : true}
                            />
                        )
                    }
                }
                // {
                //     dataField: "awb_file_exist",
                //     text: t("Inbound AWB"),
                //     formatter: (cell: string, row: Record<string, any>) => {
                //         return <>
                //             {cell && cell !== "" ? cell : "-"}
                //         </>
                //     }
                // },
            ]
            const options = {
                sizePerPage: size,
                hideSizePerPage: false,
                hidePageListOnlyOnePage: true,
                sizePerPageOptionRenderer,
                showTotal: true,
                pageListRenderer,
                paginationTotalRenderer: CustomTotal
            }
            return (
                <>
                    {selectedParcels?.length > 0 && Number(row?.awb_status) !== 1 ? (
                        <Row className="mx-4 px-0">
                            <Col sm={12} className="d-flex align-items-center justify-content-end">
                                <Button
                                    className="light-blue-color"
                                    onClick={() => regenerateLabels(row?.list_id)}
                                    disabled={reqLoading}>
                                    {reqLoading ? (
                                        <div className="spinner-border text-light" role="status"></div>
                                    ) : (
                                        t("Regenerate Labels")
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    ) : null}
                    {toast?.msg && (
                        <ToastContainer className="p-3" position={"top-end"}>
                            <Toast onClose={() => setToast({})}>
                                <Toast.Header closeButton className="bg-light-green border-bottom-0">
                                    <div className="mr-auto d-flex align-items-center">
                                        {toast?.severity === "success" ? (
                                            <img src={toast?.severity === "success" ? CheckCircleIcon : ""} />
                                        ) : null}
                                        <span className="text-black mx-1">{t(toast.msg)}</span>
                                    </div>
                                </Toast.Header>
                            </Toast>
                        </ToastContainer>
                    )}
                    <BootstrapTable
                        keyField="parcel_id"
                        data={row?.parcels}
                        columns={columns as ColumnDescription<any, any>[]}
                        wrapperClasses="inner-table my-3 mx-4"
                        headerClasses="header-class"
                        bordered={false}
                        noDataIndication={t("No_Record")}
                        rowClasses="inner-table-row"
                        pagination={paginationFactory(options)}
                        condensed={true}
                        selectRow={Number(row?.awb_status) !== 1 ? selectRow : undefined}
                    />
                </>
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    return (
        <div className="table-responsive px-4">
            {orderColumns && (
                <ToolkitProvider
                    keyField={parcels ? "parcel_id" : "list_id"}
                    data={orders}
                    columns={orderColumns}
                    search={{ searchFormatted: true }}
                    //exportCSV={{
                    //    fileName: "ConsolidationsTable.csv",
                    //    noAutoBOM: false,
                    //    exportAll: false,
                    //    onlyExportFiltered: true
                    //}}
                >
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search align-items-center justify-content-between">
                                <Col xs={12} sm={12} md={12} lg={4} className="d-flex justify-content-end">
                                    <input
                                        className="form-control"
                                        type="text"
                                        placeholder={"Search"}
                                        name="keyword"
                                        value={keyword}
                                        onChange={(e) => setKeyword(e?.target?.value)}
                                    />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={2}>
                                    <Button
                                        id="btn-primary"
                                        className="btn btn-primary w-100"
                                        type="submit"
                                        onClick={(e: any) => onSearch()}>
                                        {t("Search")}
                                    </Button>
                                </Col>
                                {/*<Col className="d-none">
                                    <ExportCSVButton {...props.csvProps}>
                                        Export
                                    </ExportCSVButton>
                                </Col>*/}
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    {orders?.length > 0 && (
                                        <RemotePagination
                                            data={orders}
                                            page={page || 1}
                                            sizePerPage={size || 250}
                                            totalSize={total}
                                            onTableChange={handleTableChange}
                                            columns={orderColumns}
                                            keyField={parcels ? "parcel_id" : "list_id"}
                                            expandRow={expandRow}
                                            props={props}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default IncomingOrdersTable
